import React from 'react';

// Render **bold** within a text string into JSX
const renderInline = (text) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold text-white">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
};

// Convert a markdown-ish string into structured JSX
export const renderMarkdown = (raw) => {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (line === '') {
      i++;
      continue;
    }

    // Headings
    const h1 = line.match(/^#\s+(.*)$/);
    const h2 = line.match(/^##\s+(.*)$/);
    const h3 = line.match(/^###\s+(.*)$/);
    if (h3) {
      blocks.push({ type: 'h3', text: h3[1] });
      i++;
      continue;
    }
    if (h2) {
      blocks.push({ type: 'h2', text: h2[1] });
      i++;
      continue;
    }
    if (h1) {
      blocks.push({ type: 'h1', text: h1[1] });
      i++;
      continue;
    }

    // Bulleted list
    if (line.startsWith('* ') || line.startsWith('- ')) {
      const items = [];
      while (
        i < lines.length &&
        (lines[i].trim().startsWith('* ') || lines[i].trim().startsWith('- '))
      ) {
        items.push(lines[i].trim().replace(/^[*-]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
      continue;
    }

    // Paragraph (consume until blank line or new block)
    const paragraph = [line];
    i++;
    while (i < lines.length) {
      const nxt = lines[i].trim();
      if (
        nxt === '' ||
        nxt.startsWith('#') ||
        nxt.startsWith('* ') ||
        nxt.startsWith('- ')
      ) {
        break;
      }
      paragraph.push(nxt);
      i++;
    }
    blocks.push({ type: 'p', text: paragraph.join(' ') });
  }

  return blocks.map((b, idx) => {
    if (b.type === 'h1') {
      return (
        <h1
          key={idx}
          className="text-[40px] lg:text-[52px] leading-[1.05] tracking-[-0.02em] font-semibold text-white mt-16 first:mt-0 mb-6"
        >
          {renderInline(b.text)}
        </h1>
      );
    }
    if (b.type === 'h2') {
      return (
        <h2
          key={idx}
          className="text-[24px] lg:text-[28px] leading-tight font-semibold text-white mt-12 mb-4 tracking-[-0.01em]"
        >
          {renderInline(b.text)}
        </h2>
      );
    }
    if (b.type === 'h3') {
      return (
        <h3
          key={idx}
          className="text-[18px] lg:text-[20px] leading-snug font-semibold text-white mt-8 mb-3"
        >
          {renderInline(b.text)}
        </h3>
      );
    }
    if (b.type === 'ul') {
      return (
        <ul key={idx} className="mt-2 mb-4 space-y-1.5 list-none">
          {b.items.map((it, j) => (
            <li
              key={j}
              className="text-[15px] text-neutral-300 leading-relaxed flex gap-3"
            >
              <span className="mt-2 w-1 h-1 rounded-full bg-blue-500 flex-shrink-0" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <p
        key={idx}
        className="text-[15px] lg:text-[16px] text-neutral-300 leading-relaxed mt-3"
      >
        {renderInline(b.text)}
      </p>
    );
  });
};
